// Numbers spelled out, for headline copy.
//
// The library's headlines are written in words, not figures — "Browse all
// nineteen kinds", "Nine collections · one hundred and sixty-four books",
// "Collection one". Every one of those numbers is live data, so the copy has to
// be generated. Figures stay in figures wherever the design uses them
// ("164 books", "268 pp", "Showing 18 of 41") — those are labels, not prose.

const ONES = [
  "zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine",
  "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen",
  "seventeen", "eighteen", "nineteen",
];

const TENS = [
  "", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety",
];

/**
 * `inWords(19) → "nineteen"`, `inWords(164) → "one hundred and sixty-four"`.
 *
 * Handles 0–9999, which is well past any real shelf size. Falls back to the
 * figure outside that range rather than throwing — a headline should degrade to
 * "Browse all 12000 kinds", never crash the page.
 */
export function inWords(n: number): string {
  if (!Number.isFinite(n) || n < 0 || n > 9999 || !Number.isInteger(n)) return String(n);
  if (n < 20) return ONES[n];
  if (n < 100) {
    const unit = n % 10;
    return unit === 0 ? TENS[Math.floor(n / 10)] : `${TENS[Math.floor(n / 10)]}-${ONES[unit]}`;
  }
  if (n < 1000) {
    const rest = n % 100;
    const head = `${ONES[Math.floor(n / 100)]} hundred`;
    return rest === 0 ? head : `${head} and ${inWords(rest)}`;
  }
  const rest = n % 1000;
  const head = `${inWords(Math.floor(n / 1000))} thousand`;
  if (rest === 0) return head;
  // "one thousand and twelve" but "one thousand, six hundred and twelve".
  return rest < 100 ? `${head} and ${inWords(rest)}` : `${head}, ${inWords(rest)}`;
}

/** Sentence-leading form: `capitalise(inWords(11))` → "Eleven". */
export function inWordsCapitalised(n: number): string {
  const word = inWords(n);
  return word.charAt(0).toUpperCase() + word.slice(1);
}

const ORDINALS = [
  "", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten",
  "eleven", "twelve",
];

/**
 * The number in a "Collection one" label. Deliberately cardinal, not ordinal —
 * the design reads "Collection one", the way a book's parts are numbered, not
 * "Collection first". Beyond twelve it falls back to the figure, because
 * "Collection thirteen" starts to read worse than "Collection 13".
 */
export function collectionOrdinal(n: number): string {
  return ORDINALS[n] ?? String(n);
}

/** "1,612" — thousands separators for figures shown as figures. */
export function grouped(n: number): string {
  return n.toLocaleString("en-US");
}

/** "165th", "1st", "22nd", "13th". Figures, not words — the design's closing
 *  CTA reads "The 165th book on this shelf could be yours." */
export function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${grouped(n)}th`;
  switch (n % 10) {
    case 1: return `${grouped(n)}st`;
    case 2: return `${grouped(n)}nd`;
    case 3: return `${grouped(n)}rd`;
    default: return `${grouped(n)}th`;
  }
}

/** `plural(1, "book")` → "1 book"; `plural(18, "book")` → "18 books". */
export function plural(n: number, singular: string, pluralForm?: string): string {
  return `${grouped(n)} ${n === 1 ? singular : (pluralForm ?? `${singular}s`)}`;
}
